import { Link } from "react-router-dom";
import { Bell, LogOut, SlidersHorizontal, Tablet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuthContext } from "@/contexts/AuthContext";
import { useRBAC } from "@/hooks/useRBAC";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";


interface TopBarProps {
  left?: React.ReactNode;
}

export function TopBar({ left }: TopBarProps = {}) {
  const { profile, signOut } = useAuthContext();
  const { can } = useRBAC();

  const initials = profile?.full_name
    ? profile.full_name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "??";

  return (
    <header
      className="border-b border-sidebar-border shrink-0 flex items-center justify-between px-6 py-3"
      style={{ backgroundColor: "hsl(220, 18%, 10%)" }}
    >
      {/* Left — caller-provided slot (e.g. mobile menu button); sidebar holds the brand. */}
      <div className="flex items-center gap-2">{left}</div>


      {/* Right — admin controls + user */}
      <div className="flex items-center gap-1">
        {/* Cross-link to the operator PWA. Same hostname, different mount; opens in a
            new tab so the office user doesn't lose their dashboard context. */}
        <a href="/operator/" target="_blank" rel="noopener noreferrer">
          <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground gap-1.5">
            <Tablet className="h-4 w-4" />
            <span className="hidden sm:inline">Operator app</span>
          </Button>
        </a>

        {can("manage:org") && (
          <Link to="/settings">
            <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground">
              <SlidersHorizontal className="h-4 w-4" />
            </Button>
          </Link>
        )}

        {/* TODO: wire notifications. The bell is a UI stub — no backend table,
            no /api/notifications, no web push subscription yet. See README
            "Notifications — not implemented" for the scope. For now, tooltip
            makes the stub status visible to admins. */}
        <Button
          variant="ghost"
          size="icon"
          disabled
          title="Notifications — not yet implemented"
          aria-label="Notifications (not yet implemented)"
          className="text-muted-foreground opacity-50"
        >
          <Bell className="h-4 w-4" />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-full">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                  {initials}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {profile && (
              <>
                <div className="px-3 py-2">
                  <p className="text-sm font-medium">{profile.full_name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{profile.role.replace(/_/g, " ")}</p>
                </div>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem onClick={() => void signOut()} className="text-destructive">
              <LogOut className="h-4 w-4 mr-2" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
